<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="auto"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="button-datepicker"
        height="48"
        width="200"
        color="#fff"
        depressed
        v-bind="attrs"
        v-on="on"
      >
        <font-awesome-icon color="#FF9100" icon="calendar-alt" />
        <span class="font-weight-light">
          {{ btnText }}
        </span>
      </v-btn>
    </template>
    <v-card>
      <v-tabs :value="dateUnit" vertical @change="handleDateUnitChange">
        <v-tab>Неделя</v-tab>
        <v-tab>Месяц</v-tab>
        <v-tab>Год</v-tab>
        <v-tabs-items v-model="dateUnit">
          <v-tab-item>
            <div class="date-picker">
              <v-date-picker
                v-model="datesRange"
                event-color="#FF9D00"
                :first-day-of-week="1"
                no-title
                locale="ru"
                reactive
                range
                @input="changeDate"
              >
              </v-date-picker>
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="date-picker">
              <v-date-picker
                v-model="month"
                event-color="#FF9D00"
                :first-day-of-week="1"
                no-title
                locale="ru"
                reactive
                type="month"
                @change="selectDate"
              >
              </v-date-picker>
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="date-picker">
              <datepicker
                v-model="year"
                minimum-view="year"
                maximum-view="year"
                format="yyyy"
                inline
                calendar-class="year-picker"
                @input="selectDate"
              ></datepicker>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </v-menu>
</template>

<script>
  import Datepicker from 'vuejs-datepicker'
  import PeriodType from '@/models/enums/statistics/PeriodType'
  import {
    format,
    startOfWeek,
    endOfWeek,
    parse,
    subMonths,
    subYears,
  } from 'date-fns'

  export default {
    name: 'PeriodDatePicker',
    components: { Datepicker },
    props: {
      initialValue: {
        type: [String, Number, Array],
        default: null,
      },
      periodType: {
        type: String,
        default: PeriodType.Week,
      },
    },
    data () {
      return {
        month: null,
        year: new Date(),
        datesRange: [],
        menu: false,
        dateUnit: 0,
      }
    },
    computed: {
      btnText () {
        switch (this.dateUnit) {
          case 0:
            return this.makeWeekPeriodText()
          case 1:
            return this.makeMonthPeriodText()
          default:
            return this.makeYearPeriodText()
        }
      },
    },
    created () {
      this.initDate()
    },
    methods: {
      getDatesRange (date) {
        return [
          format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          format(endOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        ]
      },
      initDate () {
        const date = new Date()

        switch (this.periodType) {
          case PeriodType.Week:
            this.dateUnit = 0
            this.datesRange = this.initialValue
              ? this.initialValue
              : this.getDatesRange(date)
            break
          case PeriodType.Month:
            this.dateUnit = 1
            this.month = this.initialValue
              ? this.initialValue
              : format(date, 'yyyy-MM')
            break
          case PeriodType.Year:
            this.dateUnit = 2
            this.year = this.initialValue ? this.initialValue : date
            break
        }

        this.selectDate()
      },
      handleDateUnitChange (value) {
        const date = new Date()

        if (!this.month) {
          this.month = format(date, 'yyyy-MM')
        }

        if (!this.year) {
          this.year = date
        }

        if (!this.datesRange || !this.datesRange.length) {
          this.datesRange = this.dathis.getDatesRange(date)
        }

        this.dateUnit = value

        this.selectDateWithoutClosingMenu()
      },
      changeDate (value) {
        const date = new Date(value)
        this.datesRange = this.getDatesRange(date)

        this.selectDate()
      },
      updatePeriodType () {
        switch (this.dateUnit) {
          case 0:
            this.$emit('update:periodType', PeriodType.Week)
            break
          case 1:
            this.$emit('update:periodType', PeriodType.Month)
            break
          case 2:
            this.$emit('update:periodType', PeriodType.Year)
            break
        }
      },
      selectDate () {
        this.selectDateWithoutClosingMenu()

        this.menu = false
      },
      selectDateWithoutClosingMenu () {
        this.updatePeriodType()

        switch (this.dateUnit) {
          case 0:
            this.$emit('selected', this.datesRange)
            break
          case 1:
            this.$emit('selected', this.month)
            break
          case 2:
            this.$emit('selected', new Date(this.year).getFullYear())
            break
        }
      },
      makeWeekPeriodText () {
        if (!this.datesRange.length) {
          return 'Выберите дату'
        }

        const formattedStartDate = format(
          new Date(this.datesRange[0]),
          'dd.MM.yyyy',
        )
        const formattedEndDate = format(
          new Date(this.datesRange[1]),
          'dd.MM.yyyy',
        )

        return `${formattedStartDate} - ${formattedEndDate}`
      },
      makeMonthPeriodText () {
        if (!this.month) {
          return 'Выберите дату'
        }

        const date = parse(this.month, 'yyyy-MM', new Date())

        const formattedDate = new Date(date).toLocaleDateString('ru', {
          month: 'short',
          year: 'numeric',
        })

        const formattedPrevDate = new Date(subMonths(date, 1)).toLocaleDateString(
          'ru',
          {
            month: 'short',
            year: 'numeric',
          },
        )

        return `${formattedDate} / ${formattedPrevDate}`
      },
      makeYearPeriodText () {
        if (!this.year) {
          return 'Выберите дату'
        }

        const date = new Date(this.year)

        return `${date.getFullYear()} / ${subYears(date, 1).getFullYear()}`
      },
    },
  }
</script>

<style lang="less" scoped>
.date-picker {
  border-left: 1px solid #949191;
}

.button-datepicker {
  font-size: 14px;
  border-radius: 10px;

  span {
    font-size: 12px;
    font-weight: 400;
  }

  svg {
    margin-right: 5px;
  }
}

::v-deep {
  .year-picker {
    border: none;
    padding: 0 12px;

    & header span:nth-child(2) {
      font-weight: bold;
    }

    & .cell {
      position: relative;
      border-radius: 4px;
      height: 41px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 10px 0;

      &.selected {
        background-color: #e91e63;
        color: #fff;

        &:hover {
          background-color: #e91e63;
          color: #fff;
        }
      }

      &:before {
        border-radius: inherit;
        bottom: 0;
        color: inherit;
        content: '';
        left: 0;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        right: 0;
        top: 0;
      }

      &:hover {
        &:before {
          background-color: #000;
          opacity: 0.04;
        }
      }
    }

    & .cell:not(.blank):not(.disabled).day:hover,
    & .cell:not(.blank):not(.disabled).month:hover,
    & .cell:not(.blank):not(.disabled).year:hover {
      border-color: #fff;
    }
  }
}
</style>
